import { getRouteApi } from '@tanstack/react-router'
import { formatLabel } from "@/utils/utils"
import type { ActiveFilter, Label, ListingSearch } from "@/types/types.ts"

/**
 * Thrown when the backend reports that a pagination cursor was rejected by
 * Confluence (HTTP 409 / `policy_library_invalid_cursor`). Cursors are opaque,
 * perishable tokens tied to a Confluence search-index version, so a cached one
 * can outlive its validity. The listing catches this to fall back to page 1
 * rather than surfacing a hard error.
 */
export class CursorExpiredError extends Error {
    constructor(message = 'The pagination cursor is no longer valid.') {
        super(message)
        this.name = 'CursorExpiredError'
    }
}

/**
 * React Hook to access functions to change the listing filters,
 * in addition to accessing the current values of the listing filters.
 * Avoids prop drilling.
 */
const route = getRouteApi('/')
export function useListingFilters() {
    const search = route.useSearch();
    const navigate = route.useNavigate();

    const updateFilters = (
        updates: Partial<ListingSearch>,
        options?: { resetPage?: boolean }
    ) => {
        navigate({
            search: (prev) => ({
                ...prev,
                ...updates,
                pg: options?.resetPage ? 1 : updates.pg ?? prev.pg,
            }),
            // Changing filters invalidates the pagination position, so drop the
            // cursor, page, and direction and start over at page 1.
            ...(options?.resetPage
                ? { state: (prev) => ({ ...prev, cursor: undefined, page: undefined, direction: undefined }) }
                : {}),
        })
    }

    /* Move to an adjacent page. The cursor, page number, and direction are
       written together into history state so they stay in lockstep on
       back/forward; `pg` stays in the URL purely to guarantee a distinct history
       entry. `direction` tells Confluence which way to read from the cursor. */
    const goToPage = (page: number, cursor?: string, direction?: 'next' | 'prev') => {
        navigate({
            search: (prev) => ({ ...prev, pg: page > 1 ? page : undefined }),
            state: (prev) => ({ ...prev, cursor, page, direction }),
        })
    }

    /* Bail back to the first page when a cursor is rejected (e.g. it expired at
       the source). Keeps the active filters, drops the pagination position, and
       replaces the current history entry so the broken page isn't left in the
       back/forward stack. */
    const resetPagination = () => {
        navigate({
            replace: true,
            search: (prev) => ({ ...prev, pg: undefined }),
            state: (prev) => ({ ...prev, cursor: undefined, page: undefined, direction: undefined }),
        })
    }

    /* Set the search filter */
    const setQuery = (search?: string) => {
        updateFilters(
            { search: search || undefined },
            { resetPage: true }
        )
    }

    /* Set the alpha filter */
    const setLetter = (letter?: string) => {
        updateFilters(
            { letter: letter || undefined },
            { resetPage: true }
        )
    }

    /* Set the category filter (allows multiple values) */
    const toggleLabel = (labelId: string) => {
        const labels = search.cat ?? []
        const next = labels.includes(labelId)
            ? labels.filter((id) => id !== labelId)
            : [...labels, labelId]

        updateFilters(
            { cat: next.length ? next : undefined },
            { resetPage: true }
        )
    }

    /* Clear all filters (used for active filters) */
    const clearFilters = () => {
        updateFilters(
            {
                search: undefined,
                cat: undefined,
                letter: undefined,
            },
            { resetPage: true }
        )
    }

    /* remove a specific filter (used for active filters) */
    const removeFilter = (
        type: 'letter' | 'search' | 'cat',
        value?: string
    ) => {
        switch (type) {
            case 'letter':
                updateFilters(
                    { letter: undefined },
                    { resetPage: true }
                )
                break

            case 'search':
                updateFilters(
                    { search: undefined },
                    { resetPage: true }
                )
                break

            case 'cat': {
                const next = search.cat?.filter((id) => id !== value)
                updateFilters(
                    { cat: next?.length ? next : undefined },
                    { resetPage: true }
                )
                break
            }
        }
    }

    return {
        search,
        navigate,
        updateFilters,
        setQuery,
        setLetter,
        toggleLabel,
        clearFilters,
        removeFilter,
        goToPage,
        resetPagination
    }
}

/**
 * Build the filter-only query string (no leading `?`, no `pg`). Pagination is
 * driven by cursor + direction, which are appended separately at fetch time, so
 * `pg` is deliberately excluded here.
 */
export function buildFilterQueryString(filters: ListingSearch): string {
    const params = new URLSearchParams()

    if (filters.search) {
        params.set('search', filters.search)
    }

    if (filters.letter) {
        params.set('letter', filters.letter)
    }

    if (filters.cat?.length) {
        params.set('cat', filters.cat.join(','))
    }

    return params.toString()
}

/**
 * Determine whether there are any active filters, to decide whether
 * to display the active filters component
 */
export function hasActiveFilters(filters: ListingSearch): boolean {
    return Boolean(
        filters.search ||
        filters.letter ||
        filters.cat?.length
    )
}

/**
 * Build a human-readable summary of the current result page, e.g.
 * "Displaying 26–50 of 412 results". The range is derived from the page number
 * and page size; `size` and `totalSize` come from the API response.
 */
export function getResultsSummary({
                                      currentPage,
                                      limit,
                                      size,
                                      totalSize,
                                  }: {
    currentPage: number
    limit: number
    size: number
    totalSize: number
}): string | undefined {
    if (!totalSize || size === 0) {
        return
    }

    const start = (currentPage - 1) * limit + 1
    const end = start + size - 1

    if (start === end) {
        return `Displaying ${start} of ${totalSize} results`
    }

    return `Displaying ${start}\u2013${end} of ${totalSize} results`
}



/**
 * Helper function to get all active filters
 * Used in the Active Filters component
 */
export function getActiveFilters(
    filters: ListingSearch,
    labels: Label[] = []
): ActiveFilter[] {
    const labelMap = new Map(
        labels.map((label) => [label.name, formatLabel(label.name)])
    )

    const activeFilters: ActiveFilter[] = []

    if (filters.search) {
        activeFilters.push({
            label: filters.search,
            type: 'search',
            value: filters.search,
        })
    }

    if (filters.letter) {
        activeFilters.push({
            label: filters.letter,
            type: 'letter',
            value: filters.letter,
        })
    }

    filters.cat?.forEach((catName) => {
        activeFilters.push({
            label: labelMap.get(catName) ?? formatLabel(catName),
            type: 'cat',
            value: catName,
        })
    })


    //Alphabetize filters
    return activeFilters.sort((a, b) =>
        a.label.localeCompare(b.label)
    )
}